import { z } from 'zod';

// Helper validator for custom fields
const createCustomFieldsValidator = (activeDefinitions) => {
    return z.array(
        z.object({
            field_id: z.string(),
            value: z.any()
        })
    ).superRefine((customFields, ctx) => {
        activeDefinitions.forEach(def => {
            const fieldEntry = customFields.find(cf => cf.field_id === def.field_id);
            const value = fieldEntry?.value;

            // Required check
            if (def.is_required && (value === undefined || value === null || value === '')) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${def.label} is required`,
                    path: ['custom_fields', def.field_id] // Access path logic might need adjustment based on form structure
                });
            }

            if (value !== undefined && value !== null && value !== '') {
                // Type specific validation (basic checks)
                // Numeric checks for min/max would ideally happen here if we forced type conversion,
                // but often 'value' is stored as string/number mixed in form state.
                // We'll trust the individual field validators in the UI mostly, but this serves as a schema backup.

                if (def.validation_rules) {
                    def.validation_rules.forEach(rule => {
                        if (rule.rule_type === 'regex' && typeof value === 'string') {
                            try {
                                const regex = new RegExp(rule.value);
                                if (!regex.test(value)) {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: rule.error_message || 'Invalid format',
                                        path: ['custom_fields', def.field_id]
                                    });
                                }
                            } catch (e) { }
                        }
                    });
                }
            }
        });
    });
};

// Sub-schemas for arrays
const vehicleSchema = z.object({
    vehicle_year: z.coerce.number().min(1900).max(2100).optional(),
    vehicle_make: z.string().min(1, "Make is required"),
    vehicle_model: z.string().min(1, "Model is required"),
    vehicle_license_plate: z.string().optional(),
    vehicle_vin: z.string().optional(),
    vehicle_color: z.string().optional(),
    vehicle_type: z.string().optional(),
    vehicle_state_registered: z.string().optional()
});

const boatSchema = z.object({
    boat_year: z.coerce.number().min(1900).max(2100).optional(),
    boat_make: z.string().min(1, "Make is required"),
    boat_model: z.string().min(1, "Model is required"),
    boat_length: z.coerce.number().positive().optional(),
    boat_name: z.string().optional(),
    boat_tx_number: z.string().optional(),
    boat_hin: z.string().optional(),
    motor_type: z.string().optional(),
    motor_horse_power: z.coerce.number().optional()
});

const trailerSchema = z.object({
    trailer_year: z.coerce.number().min(1900).max(2100).optional(),
    trailer_make: z.string().min(1, "Make is required"),
    trailer_model: z.string().optional(),
    trailer_license_plate: z.string().optional(),
    trailer_length: z.coerce.number().optional()
});

const rvSchema = z.object({
    rv_year: z.coerce.number().min(1900).max(2100).optional(),
    rv_make: z.string().min(1, "Make is required"),
    rv_model: z.string().min(1, "Model is required"),
    rv_class: z.string().optional(), // Class A, B, C, Fifth Wheel
    rv_length: z.coerce.number().optional(),
    rv_license_plate: z.string().optional()
});

export const customerBaseSchema = z.object({
    // Split name fields — canonical post-2026-06-01. The operator form
    // auto-derives full_name = `${first_name} ${last_name}` so the bridge
    // never has to guess where the last-name boundary is (PTI Name field
    // is "LastName,FirstName" and naive whitespace splits break on
    // "Van Houten", "De La Cruz", "St. John", etc.).
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    full_name: z.string().min(2, "Full Name is required"),
    email: z.string().email("Invalid email address").optional().or(z.literal('')),
    phone: z.string().min(10, "Phone number must be at least 10 digits"),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip_code: z.string().optional(),

    // PTI-style gate PIN. 4-6 digits is the Falcon XT keypad standard;
    // empty string is allowed (operator can leave it blank until known).
    gate_code: z
        .string()
        .regex(/^\d{4,6}$/, 'Gate code must be 4 to 6 digits')
        .optional()
        .or(z.literal('')),

    // Arrays
    vehicles: z.array(vehicleSchema).default([]),
    boats: z.array(boatSchema).default([]),
    trailers: z.array(trailerSchema).default([]),
    rvs: z.array(rvSchema).default([]),

    // Custom fields placeholder (validated dynamically)
    custom_fields: z.array(z.object({
        field_id: z.string(),
        value: z.any()
    })).default([])
});

// Function to get the full schema with dynamic rules
export const getCustomerSchema = (customFieldDefinitions = []) => {
    if (!customFieldDefinitions || customFieldDefinitions.length === 0) {
        return customerBaseSchema;
    }

    // Extend base schema with superRefine for custom fields
    // Note: We don't overwrite the type definition of custom_fields, just add refinement
    // But Zod structure is immutable, so we create a new schema that intersects or refines
    // For simplicity with react-hook-form, easiest is to pass the schema generator to useForm

    return customerBaseSchema.superRefine((data, ctx) => {
        customFieldDefinitions.forEach(def => {
            const fieldEntry = data.custom_fields?.find(cf => cf.field_id === def.field_id);
            const value = fieldEntry?.value;

            if (def.is_required && (value === undefined || value === null || value === '')) {
                // We add the error to the specific field path to make it show up in UI forms
                // The path convention here assumes react-hook-form structure like `custom_fields_values.${field_id}`
                // or similar. If we bind strictly to the array, the path is tricky. 
                // Strategy: The UI will likely map fields by ID. We can report errors at root or path.

                // Let's assume the form data structure for custom fields is an array in `custom_fields`.
                // Finding the index is hard if it doesn't exist.
                // A common pattern is to validate and put errors in a known key map.

                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${def.label} is required`,
                    path: ['custom_fields', def.field_id] // Using ID as path key for easier UI lookup
                });
            }

            // Regex validation example
            if (value && def.validation_rules) {
                def.validation_rules.forEach(rule => {
                    if (rule.rule_type === 'regex' && typeof value === 'string' && rule.value) {
                        if (!new RegExp(rule.value).test(value)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: rule.error_message || 'Format is invalid',
                                path: ['custom_fields', def.field_id]
                            });
                        }
                    }
                });
            }
        });
    });
};
